/**
 * Sidebar Recent rail read model (`GET /recent-conversations`).
 *
 * One page mixes knowledge threads, started chat rooms and studies. Items carry public
 * tokens only, never database row ids.
 */

import type { StudyThreadOrigin } from './study-thread-origin';

export interface RecentThreadItem {
  kind: 'thread';
  token: string;
  title: string;
  /** ISO timestamp: last message time, or the thread's update time. */
  lastActivityAt: string;
  /**
   * Sort key: UTC fixed width `YYYY-MM-DDTHH:MM:SS.ffffffZ`, the later of the
   * last open and the last activity.
   */
  recentAt: string;
  pinned: boolean;
  isOwner: boolean;
  /**
   * A teammate's shared thread the caller has not opened since it was shared
   * (the sidebar "new" dot). Optional so older payloads read as not new.
   */
  isNew?: boolean;
}

export interface RecentRoomItem {
  kind: 'room';
  token: string;
  /** Null when the room has no title yet; the client localizes a fallback. */
  title: string | null;
  roomType: 'group' | 'one_on_one';
  participantCount: number;
  firstParticipantName: string | null;
  /** ISO timestamp of the room's last message. */
  lastActivityAt: string;
  /** Sort key, same format as `RecentThreadItem.recentAt`. */
  recentAt: string;
  /** Caller pin. Absent reads as unpinned. Pinned rows sort ahead of recency. */
  pinned?: boolean;
}

export interface RecentStudyItem {
  kind: 'study';
  /** Experiment public id or questionnaire draft token. */
  token: string;
  /** Null when untitled; the client localizes a fallback. */
  title: string | null;
  studyKind: 'experiment' | 'draft';
  /** Quick rows reopen their existing thread origin; absent retains full-study navigation. */
  presentation?: 'quick';
  /** Only returned when the caller can still access the originating thread. */
  threadOrigin?: StudyThreadOrigin;
  draftRevision?: number;
  goalId?: string | null;
  status: string;
  /** ISO timestamp of the study's own activity. */
  lastActivityAt: string;
  /** Sort key, same format as `RecentThreadItem.recentAt`. */
  recentAt: string;
  /** Caller pin. Absent reads as unpinned. Pinned rows sort ahead of recency. */
  pinned?: boolean;
}

export type RecentConversationItem = RecentThreadItem | RecentRoomItem | RecentStudyItem;

/** Kinds a user can pin from the Recent rail (`POST /recent-conversations/pin`). */
export const RECENT_PIN_KINDS = ['room', 'experiment', 'draft'] as const;
export type RecentPinKind = (typeof RECENT_PIN_KINDS)[number];

/** Body of `POST /recent-conversations/pin`. Threads pin through the knowledge API. */
export interface SetRecentPinRequest {
  kind: RecentPinKind;
  token: string;
  pinned: boolean;
}

/** Experiment statuses that render as running in the rail. */
export const ACTIVE_EXPERIMENT_STATUSES = ['pending', 'preprocessing_assets', 'running'] as const;

/** Kinds a user can record as opened (`POST /recent-conversations/open`). */
export const RECENT_OPEN_KINDS = ['thread', 'room', 'experiment', 'draft'] as const;
export type RecentOpenKind = (typeof RECENT_OPEN_KINDS)[number];

/** Body of `POST /recent-conversations/open`; an invisible target returns 404. */
export interface RecordRecentOpenRequest {
  kind: RecentOpenKind;
  token: string;
}

function normalizeRecentAt(value: string | undefined | null): string {
  if (!value) {
    return '';
  }
  const match = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:?\d{2})?$/.exec(
    value,
  );
  if (!match) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed)
      ? ''
      : `${new Date(parsed).toISOString().slice(0, 19)}.${String(parsed % 1000).padStart(3, '0')}000Z`;
  }
  const [, date, time, fraction = '', zone] = match;
  const micros = fraction.padEnd(6, '0');
  if (zone && zone !== 'Z' && !/^[+-]00:?00$/.test(zone)) {
    const parsed = Date.parse(value);
    if (Number.isNaN(parsed)) {
      return '';
    }
    return `${new Date(parsed).toISOString().slice(0, 19)}.${micros}Z`;
  }
  return `${date}T${time}.${micros}Z`;
}

/**
 * Orders two sort keys ascending: negative when `a` is older than `b`, so a
 * newest-first sort is `items.sort((x, y) => compareRecentAt(y.recentAt, x.recentAt))`.
 * Accepts server keys (microseconds, with or without `Z`) and client
 * `toISOString()` values (milliseconds, `Z`). Unparseable values sort oldest.
 */
export function compareRecentAt(a: string | undefined | null, b: string | undefined | null): number {
  const left = normalizeRecentAt(a);
  const right = normalizeRecentAt(b);
  if (left === right) {
    return 0;
  }
  return left < right ? -1 : 1;
}

export interface RecentConversationsPage {
  items: Array<RecentConversationItem>;
  /** Opaque cursor for the next page; null when both sources are exhausted. */
  nextCursor: string | null;
}
